import React, { useMemo } from 'react'
import styled from 'styled-components'
import { sortBy } from 'lib/array/arrayUtils'
import useQueryParams from 'hooks/useQueryParams'
import { getCheckoutDate } from 'selectors/offerPage/offerPageSelectors'
import { OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LayoutContainer from 'components/Common/LayoutContainer'
import BedbankRoomOptionMobile from './BedbankRoomOptionMobile'
import { GOOGLE_ADS_UTM_CONTENT } from 'constants/bedbank'
import Pane from 'components/Common/Pane'
import { preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'

const RoomsLayoutContainer = styled(LayoutContainer)`
  padding: 0;
`

interface Props {
  checkInDate?: string;
  checkOutDate?: string;
  offer: App.BedbankOffer;
  onViewPackageOptions: () => void;
  packageViews: Array<App.BedbankOfferPackageView>;
  fetchingRates?: boolean;
}

function BedbankRoomOptionsMobile(props: Props) {
  const {
    checkInDate,
    checkOutDate,
    offer,
    onViewPackageOptions,
    packageViews,
    fetchingRates,
  } = props

  const query = useQueryParams()
  const validCheckinCheckoutDates = !!(checkInDate && checkOutDate)
  const roomTypeId = query.get('roomTypeId')
  const utmContent = query.get('utm_content')

  const hasPromotions = offer.promotions.length > 0
  const showAll = packageViews.filter(p => p.available).length === 0

  const sortedPackageViews = useMemo(() => {
    return sortBy(packageViews, pkg => {
      if (pkg.cheapestRate) {
        return pkg.cheapestRate.totals.inclusive + (pkg.cheapestRate.totals.propertyFees ?? 0)
      } else if (hasPromotions) {
        return Number.MAX_SAFE_INTEGER
      }

      if (validCheckinCheckoutDates) {
        if (!pkg.capacityValid) {
          return Number.MAX_SAFE_INTEGER - 1
        }

        return Number.MAX_SAFE_INTEGER
      }

      if (pkg.capacityValid) {
        return Number.MIN_SAFE_INTEGER
      }

      return Number.MAX_SAFE_INTEGER
    }, 'asc')
  }, [packageViews, validCheckinCheckoutDates, hasPromotions])

  const sortedPackages = useMemo(() => {
    const highlightedPackage = sortedPackageViews.find(p => p.package.roomId === roomTypeId)
    if (utmContent === GOOGLE_ADS_UTM_CONTENT && highlightedPackage) {
      return [
        highlightedPackage,
        sortedPackageViews.find(p => p.package.roomId !== roomTypeId),
      ].filter((p): p is App.BedbankOfferPackageView => !!p) // this fix type inference
    }
    else {
      return sortedPackageViews
    }
  }, [sortedPackageViews, utmContent, roomTypeId])

  const displayablePackages = useMemo(() => sortedPackages.filter(
    packageView => (packageView.show && packageView.available) || (packageView.show && showAll),
  ), [sortedPackages, showAll])

  return <RoomsLayoutContainer>
    <Pane background="neutral-six">
      <VerticalSpacer gap={8}>
        {displayablePackages.map(packageView => <BedbankRoomOptionMobile
          key={packageView.package.roomId}
          offer={offer}
          packageView={packageView}
          fetchingRates={fetchingRates}
          isHighlighted={utmContent === GOOGLE_ADS_UTM_CONTENT && roomTypeId === packageView.package.roomId}
          onViewPackageOptions={onViewPackageOptions}
          detailsModalMode="room-and-package"
        />)}
      </VerticalSpacer>
    </Pane>
  </RoomsLayoutContainer>
}

type MappedProps = Pick<Props, 'checkInDate' | 'checkOutDate'> & { open: boolean }

export default withOfferPageAndReduxState((state: OfferPageState): MappedProps => {
  return {
    checkInDate: state.checkInDate,
    checkOutDate: getCheckoutDate(state),
    open: !!state.activeFilter,
  }
}, BedbankRoomOptionsMobile, preventRenderWithOpenFilter)
